import { inject, Injectable, signal } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import {
  AfterDeletingLastRowBehavior,
  ComparisonQueryFormGroupValue,
  ComplexFilterService,
} from '@blaze-runtime/engine-ui-feature';
import {
  IntelliConfig,
  IntelliSenseService,
  QueryOperatorOption,
} from '@blaze-shared/intellisense-feature';
import {
  ColumnFieldOption,
  DatasetSchema,
  findColumn,
  mapColumnsToOptions,
} from '@blaze-shared/schema-domain';
import { SelectOption } from '@blaze-shared/shared-util';

@Injectable()
export class BlazeTableFilterService implements ComplexFilterService {
  private readonly iss = inject(IntelliSenseService);

  private readonly blazeTableContext$ = new BehaviorSubject<DatasetSchema | null>(null);

  readonly showIgnoreWhenEmptyToggle = signal(false);

  afterDeletingLastRow: AfterDeletingLastRowBehavior = 'insert';

  setup(blazeTableContext: DatasetSchema): void {
    this.blazeTableContext$.next(blazeTableContext);
  }

  getColumns(): Observable<SelectOption<ColumnFieldOption>[]> {
    return this.blazeTableContext$.pipe(
      filter(Boolean),
      switchMap((rollupContext) => {
        return this.iss.getOptionsForColumnsInDataset(rollupContext.id);
      }),
      map((columns) => mapColumnsToOptions(columns)),
    );
  }

  getOperators(
    queryCondition$: Observable<ComparisonQueryFormGroupValue>,
  ): Observable<QueryOperatorOption[]> {
    return combineLatest({
      queryCondition: queryCondition$.pipe(startWith(null)),
      blazeTableContext: this.blazeTableContext$.pipe(filter(Boolean)),
    }).pipe(
      concatMap(({ queryCondition, blazeTableContext }) => {
        const column = queryCondition
          ? findColumn(blazeTableContext?.columns, queryCondition.column)
          : null;
        return this.iss.getOptionsForQueryOperators(column);
      }),
    );
  }

  getIntelliConfig(
    queryCondition$: Observable<ComparisonQueryFormGroupValue>,
  ): Observable<IntelliConfig> {
    return combineLatest({
      queryCondition: queryCondition$,
      blazeTableContext: this.blazeTableContext$.pipe(filter(Boolean)),
    }).pipe(
      map(({ queryCondition, blazeTableContext }) => {
        return {
          column: findColumn(blazeTableContext.columns, queryCondition.column) || null,
          datasetSchema: blazeTableContext,
          inputSchema: blazeTableContext,
          isBlazeTableFormulaColumn: true,
          pageContextSchema: null,
          schemaColumns: blazeTableContext.columns,
          fixedValuesSchema: blazeTableContext,

          showFixedValuesForLinks: true,
          id: queryCondition.formControlId ?? queryCondition.column,
          operator: queryCondition.operator,
          valueInfo: {
            displayInfos: queryCondition.displayInfos ?? [],
            isFormulaValue: queryCondition.isFormulaValue,
            value: queryCondition.value ?? '',
          },
        } as IntelliConfig;
      }),
    );
  }

  getErrors(): Observable<string[]> {
    return of([]);
  }
}
